import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { format, parseISO, isToday, isPast } from 'date-fns';
import {
  CheckCircleIcon,
  XMarkIcon,
  PlusIcon,
  TrashIcon,
  ArrowRightIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';
import { CheckCircleIcon as CheckCircleSolid } from '@heroicons/react/24/solid';
import { getTasks, createTask, completeTask, dismissTask, deleteTask } from '../services/taskService';

const PRIORITY_ORDER = { urgent: 0, high: 1, medium: 2, low: 3 };

const PRIORITY_STYLES = {
  urgent: 'bg-danger-100 text-danger-700 dark:bg-danger-900/30 dark:text-danger-400',
  high: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400',
  medium: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
  low: 'bg-neutral-100 text-neutral-600 dark:bg-neutral-700 dark:text-neutral-400',
};

/** Build a deep-link URL for a given task type */
function taskLink(task) {
  switch (task.type) {
    case 'mark_appointment':
    case 'write_note':
      return task.appointmentId ? `/appointments` : null;
    case 'file_claim':
      return task.clientId ? `/insurance/new?clientId=${task.clientId}` : '/insurance/new';
    case 'charge_client':
      return task.relatedId ? `/billing/${task.relatedId}` : '/billing';
    case 'unsigned_note':
      return task.relatedId ? `/notes/${task.relatedId}` : '/notes';
    case 'add_card_on_file':
      return task.clientId ? `/clients/${task.clientId}?tab=billing` : null;
    default:
      return task.clientId ? `/clients/${task.clientId}` : null;
  }
}

function DueDateBadge({ dueDate }) {
  if (!dueDate) return null;
  const d = parseISO(dueDate);
  const overdue = isPast(d) && !isToday(d);
  const due = isToday(d);
  return (
    <span className={`text-xs flex items-center gap-1 ${overdue ? 'text-danger-600 dark:text-danger-400 font-semibold' : due ? 'text-orange-600 dark:text-orange-400' : 'text-neutral-500 dark:text-neutral-400'}`}>
      <ClockIcon className="w-3.5 h-3.5" />
      {overdue ? 'Overdue · ' : due ? 'Due today · ' : ''}{format(d, 'MMM d')}
    </span>
  );
}

function TaskCard({ task, onComplete, onDismiss, onDelete }) {
  const link = taskLink(task);
  const isManual = !task.isAutoGenerated;

  return (
    <div className="bg-white dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 rounded-lg px-4 py-3 flex items-start gap-3 group hover:border-primary-300 dark:hover:border-primary-600 transition-colors">
      {/* Complete button */}
      <button
        onClick={() => onComplete(task.id)}
        className="mt-0.5 shrink-0 text-neutral-300 dark:text-neutral-600 hover:text-green-500 dark:hover:text-green-400 transition-colors"
        title="Mark complete"
      >
        <CheckCircleSolid className="w-5 h-5" />
      </button>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <p className="text-sm font-medium text-neutral-900 dark:text-neutral-100 leading-snug">{task.title}</p>
          <span className={`shrink-0 text-xs font-medium px-2 py-0.5 rounded-full capitalize ${PRIORITY_STYLES[task.priority]}`}>
            {task.priority}
          </span>
        </div>
        {task.description && (
          <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-0.5 line-clamp-2">{task.description}</p>
        )}
        <div className="flex items-center gap-3 mt-1.5 flex-wrap">
          {task.client && (
            <Link
              to={`/clients/${task.client.id}`}
              className="text-xs text-primary-600 dark:text-primary-400 hover:underline"
              onClick={(e) => e.stopPropagation()}
            >
              {task.client.firstName} {task.client.lastName}
            </Link>
          )}
          <DueDateBadge dueDate={task.dueDate} />
        </div>
      </div>

      {/* Actions */}
      <div className="shrink-0 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
        {link && (
          <Link
            to={link}
            className="p-1 rounded text-neutral-400 hover:text-primary-600 dark:hover:text-primary-400"
            title="Go to"
          >
            <ArrowRightIcon className="w-4 h-4" />
          </Link>
        )}
        <button
          onClick={() => onDismiss(task.id)}
          className="p-1 rounded text-neutral-400 hover:text-yellow-600 dark:hover:text-yellow-400"
          title="Dismiss"
        >
          <XMarkIcon className="w-4 h-4" />
        </button>
        {isManual && (
          <button
            onClick={() => onDelete(task.id)}
            className="p-1 rounded text-neutral-400 hover:text-danger-600 dark:hover:text-danger-400"
            title="Delete"
          >
            <TrashIcon className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
}

function NewTaskModal({ onClose, onSave }) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [priority, setPriority] = useState('medium');

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!title.trim()) return;
    onSave({ title: title.trim(), description: description.trim() || null, dueDate: dueDate || null, priority });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={onClose}>
      <div className="bg-white dark:bg-neutral-900 rounded-xl shadow-xl w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100">New Task</h2>
          <button onClick={onClose}><XMarkIcon className="w-5 h-5 text-neutral-400" /></button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">Title *</label>
            <input
              autoFocus
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full border border-neutral-300 dark:border-neutral-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-neutral-800 text-neutral-900 dark:text-neutral-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
              placeholder="e.g. Review treatment plan for client"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">Description</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              className="w-full border border-neutral-300 dark:border-neutral-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-neutral-800 text-neutral-900 dark:text-neutral-100 focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
            />
          </div>
          <div className="flex gap-3">
            <div className="flex-1">
              <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">Due Date</label>
              <input
                type="date"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
                className="w-full border border-neutral-300 dark:border-neutral-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-neutral-800 text-neutral-900 dark:text-neutral-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <div className="flex-1">
              <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">Priority</label>
              <select
                value={priority}
                onChange={(e) => setPriority(e.target.value)}
                className="w-full border border-neutral-300 dark:border-neutral-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-neutral-800 text-neutral-900 dark:text-neutral-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </select>
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm rounded-lg border border-neutral-300 dark:border-neutral-600 text-neutral-700 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-neutral-800">Cancel</button>
            <button type="submit" className="px-4 py-2 text-sm rounded-lg bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50" disabled={!title.trim()}>Create</button>
          </div>
        </form>
      </div>
    </div>
  );
}

const STATUS_TABS = [
  { value: 'pending', label: 'Pending' },
  { value: 'completed', label: 'Completed' },
  { value: 'dismissed', label: 'Dismissed' },
];

export default function Tasks() {
  const [status, setStatus] = useState('pending');
  const [showModal, setShowModal] = useState(false);
  const qc = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['tasks', status],
    queryFn: () => getTasks({ status }),
  });

  const tasks = data?.tasks ?? [];

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['tasks'] });
    qc.invalidateQueries({ queryKey: ['taskCount'] });
  };

  const completeMut = useMutation({ mutationFn: completeTask, onSuccess: invalidate });
  const dismissMut = useMutation({ mutationFn: dismissTask, onSuccess: invalidate });
  const deleteMut = useMutation({ mutationFn: deleteTask, onSuccess: invalidate });
  const createMut = useMutation({
    mutationFn: createTask,
    onSuccess: () => { invalidate(); setShowModal(false); },
  });

  // Group by priority for pending view
  const grouped = status === 'pending'
    ? Object.entries(
        tasks.reduce((acc, t) => {
          (acc[t.priority] = acc[t.priority] || []).push(t);
          return acc;
        }, {})
      ).sort(([a], [b]) => PRIORITY_ORDER[a] - PRIORITY_ORDER[b])
    : null;

  return (
    <div className="max-w-3xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-neutral-900 dark:text-neutral-100">Tasks</h1>
          <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-0.5">
            Action items and workflow reminders
          </p>
        </div>
        <button
          onClick={() => setShowModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700 transition-colors"
        >
          <PlusIcon className="w-4 h-4" />
          New Task
        </button>
      </div>

      {/* Status tabs */}
      <div className="flex gap-1 bg-neutral-100 dark:bg-neutral-800 rounded-lg p-1 mb-6 w-fit">
        {STATUS_TABS.map((tab) => (
          <button
            key={tab.value}
            onClick={() => setStatus(tab.value)}
            className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${
              status === tab.value
                ? 'bg-white dark:bg-neutral-700 text-neutral-900 dark:text-neutral-100 shadow-sm'
                : 'text-neutral-600 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-neutral-200'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Task list */}
      {isLoading ? (
        <div className="text-center py-12 text-neutral-400">Loading…</div>
      ) : tasks.length === 0 ? (
        <div className="text-center py-16">
          <CheckCircleIcon className="w-12 h-12 text-green-400 mx-auto mb-3" />
          <p className="text-neutral-500 dark:text-neutral-400 font-medium">
            {status === 'pending' ? 'All caught up!' : `No ${status} tasks`}
          </p>
        </div>
      ) : status === 'pending' && grouped ? (
        <div className="space-y-6">
          {grouped.map(([priority, items]) => (
            <div key={priority}>
              <h3 className={`text-xs font-semibold uppercase tracking-wider mb-2 ${
                priority === 'urgent' ? 'text-danger-600 dark:text-danger-400' :
                priority === 'high' ? 'text-orange-600 dark:text-orange-400' :
                priority === 'medium' ? 'text-yellow-600 dark:text-yellow-400' :
                'text-neutral-500 dark:text-neutral-400'
              }`}>
                {priority} · {items.length}
              </h3>
              <div className="space-y-2">
                {items.map((task) => (
                  <TaskCard
                    key={task.id}
                    task={task}
                    onComplete={(id) => completeMut.mutate(id)}
                    onDismiss={(id) => dismissMut.mutate(id)}
                    onDelete={(id) => deleteMut.mutate(id)}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="space-y-2">
          {tasks.map((task) => (
            <TaskCard
              key={task.id}
              task={task}
              onComplete={(id) => completeMut.mutate(id)}
              onDismiss={(id) => dismissMut.mutate(id)}
              onDelete={(id) => deleteMut.mutate(id)}
            />
          ))}
        </div>
      )}

      {showModal && (
        <NewTaskModal
          onClose={() => setShowModal(false)}
          onSave={(data) => createMut.mutate(data)}
        />
      )}
    </div>
  );
}
